// CustomerPanel — left rail in Command Center.
//
// Replaces the old right-rail OrderContextSidebar's customer-context role.
// Layout matches v2 mockup's `cust-panel`:
//
//   Avatar (initials) + name + email/location
//   4-stat grid: Orders / Lifetime / Msgs / Customer-age
//   Order history list (last 5 with status chip)
//   Notes section (CustomerNoteBox)
//   Full conversation history (collapsed by default)
//
// Data sources:
//   - Shopify orders/customer: useOrderContext (MD-brand only)
//   - Customer note: CustomerNoteBox keyed by customer_email
//   - Past threads with same email: useCustomerHistory
//
// Empty states:
//   - No Shopify match: "First-time visitor or guest checkout" headline,
//     stats hidden, history hidden. Notes box still works.
//   - HK brand: no Shopify section at all (HK has no Shopify store).

import { useMemo } from 'react';
import { useOrderContext } from '@/lib/hooks/useOrderContext';
import { useCustomerHistory } from '@/lib/hooks/useCustomerHistory';
import { prettifyEmailLocal } from '@/lib/emailClean';
import { CustomerNoteBox } from './CustomerNoteBox';
import { DecisionPanel } from './DecisionPanel';
import { FullCustomerHistory } from './FullCustomerHistory';

export interface CustomerPanelProps {
  conversationId: string;
  brand: 'md' | 'hk';
  customerEmail: string | null;
  /** Display name to show in the panel header (falls back to email local part). */
  displayName: string;
  initials: string;
  /** Optional "City, ST" string. Renders as italic accent chip after the email. */
  location?: string | null;
}

function formatMoneyShort(amount: string, currency: string): string {
  const n = Number.parseFloat(amount);
  if (!Number.isFinite(n)) return `${amount}`;
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}k`;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(n);
}

function formatCustomerAge(firstOrderIso: string | null): string {
  if (!firstOrderIso) return '—';
  const months = Math.max(
    1,
    Math.floor((Date.now() - new Date(firstOrderIso).getTime()) / (30 * 86_400_000)),
  );
  if (months < 12) return `${months}mo`;
  const years = Math.floor(months / 12);
  return `${years}y`;
}

function formatShortDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function statusBadge(fulfillment: string, financial: string | null): string {
  const f = fulfillment.toUpperCase();
  if (f === 'FULFILLED' || f === 'SUCCESS') return 'SHIPPED';
  if (f === 'IN_TRANSIT') return 'IN TRANSIT';
  if (f === 'UNFULFILLED' || f === 'PARTIAL') {
    if (financial?.toUpperCase() === 'PAID') return 'IN PROD';
    return 'PENDING';
  }
  if (f === 'CANCELLED' || f === 'CANCELED') return 'CANCELLED';
  if (f === 'ON_HOLD') return 'ON HOLD';
  return f;
}

// Fallback chip label for legacy cache rows that don't carry sourceLabel.
function sourceFallbackLabel(src: 'md' | 'brokerage' | 'shipstation' | undefined): string {
  if (src === 'brokerage') return 'BROKERAGE';
  if (src === 'shipstation') return 'SHIPSTATION';
  return 'SHOPIFY MD';
}

export function CustomerPanel(props: CustomerPanelProps) {
  const { conversationId, brand, customerEmail, displayName, initials, location } = props;
  const isMd = brand === 'md';
  // Phase 8: ShipStation integration. We now enable the hook for HK too so
  // HarperKellee customers' Etsy orders (surfaced via ShipStation) show up.
  // The route still calls Shopify MD / Brokerage — they'll miss for HK buyers
  // and that's fine. Orders may still be populated from the ShipStation slice.
  const ctx = useOrderContext(conversationId);
  const history = useCustomerHistory(customerEmail, conversationId);

  const customer = ctx.data?.customer ?? null;
  const orders = ctx.data?.orders ?? [];
  const firstOrderIso = useMemo(() => {
    if (orders.length === 0) return null;
    return [...orders].sort((a, b) => a.createdAt.localeCompare(b.createdAt))[0]!.createdAt;
  }, [orders]);

  // history.data.messageCount is the number of OTHER conversations with this
  // email. When the query has settled (data present) we want to show the real
  // number — including 0. Only show the em-dash placeholder while loading or
  // when the query failed (data undefined).
  const messageCount: number | null = history.data ? history.data.messageCount : null;

  // Prefer Shopify's own firstName/lastName pair when we have it — that's the
  // cleanest source. Fall back to displayName, then the prop-supplied name
  // (Center derives from sender_name → prettified email local part). FINAL
  // guard: if the resolved name still looks like an email address, prettify
  // ourselves so the giant Larken header is never just "sarah.fellah@p..."
  // — the email already shows on its own line directly beneath.
  const rawHeaderName = useMemo(() => {
    if (customer?.firstName || customer?.lastName) {
      return [customer.firstName, customer.lastName].filter(Boolean).join(' ');
    }
    return customer?.displayName ?? displayName;
  }, [customer?.firstName, customer?.lastName, customer?.displayName, displayName]);
  const headerName = useMemo(() => {
    const trimmed = rawHeaderName?.trim() ?? '';
    if (!trimmed) return 'Customer';
    if (trimmed.includes('@')) {
      return prettifyEmailLocal(trimmed) ?? 'Customer';
    }
    return trimmed;
  }, [rawHeaderName]);

  // Location: prefer the prop (so the parent can override, e.g. Etsy) but
  // fall back to whatever Shopify sent in the defaultAddress.
  const headerLocation = location ?? customer?.location ?? null;

  return (
    <aside className="cust-panel" aria-label="Customer">
      <div className="cust-head">
        <div className="cust-avatar" aria-hidden>
          {initials}
        </div>
        <div className="cust-id">
          <div className="eyebrow">The customer</div>
          <div className="cust-name">{headerName}</div>
          {customerEmail ? (
            <div className="cust-meta">
              <span className="cust-email">{customerEmail}</span>
              {headerLocation ? (
                <>
                  {' \u00b7 '}
                  <span className="cust-loc">{headerLocation}</span>
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      {ctx.isLoading ? (
        <div className="cust-loading">Loading customer record…</div>
      ) : customer ? (
        <div className="cust-stats">
          <div className="cust-stat">
            <div className="n">{customer.numberOfOrders}</div>
            <div className="l">Orders</div>
          </div>
          <div className="cust-stat">
            <div className="n">
              {formatMoneyShort(
                customer.lifetimeSpend.amount,
                customer.lifetimeSpend.currencyCode,
              )}
            </div>
            <div className="l">Lifetime</div>
          </div>
          <div className="cust-stat">
            <div className="n">{messageCount ?? '—'}</div>
            <div className="l">Msgs</div>
          </div>
          <div className="cust-stat">
            <div className="n">{formatCustomerAge(firstOrderIso)}</div>
            <div className="l">Customer</div>
          </div>
        </div>
      ) : isMd ? (
        <div className="cust-empty">
          No Shopify record matches this email. First-time visitor, guest checkout, or different
          shopper email.
        </div>
      ) : (
        <div className="cust-empty">
          HarperKellee runs on Etsy — no Shopify customer record on file. Order history below pulls
          straight from ShipStation.
        </div>
      )}

      {orders.length > 0 ? (
        <div className="cust-history">
          <div className="eyebrow">Order history</div>
          {orders.slice(0, 8).map((o) => {
            const chipLabel = o.sourceLabel ?? sourceFallbackLabel(o.source);
            const chipColor = o.sourceChipColor ?? 'clay';
            // Per polish #26: deep-link to the originating system (Shopify
            // admin / Etsy seller "Your Orders") so Kimi can jump from
            // the rail to the real order page in one click. Only render
            // the link when the backend populated adminUrl — it's null
            // for Etsy HK, Amazon, MKT BOX, and legacy cached rows.
            const adminUrl = o.adminUrl ?? null;
            const adminTitle =
              chipLabel === 'ETSY MD'
                ? 'Open in Etsy'
                : chipLabel === 'BROKERAGE'
                  ? 'Open in Brokerage admin'
                  : 'Open in Shopify';
            return (
              <div className="order-row" key={o.shopifyOrderId}>
                <div className="order-row-top">
                  <div className="n">{o.name}</div>
                  <span className={`order-chip chip-${chipColor}`}>{chipLabel}</span>
                  {adminUrl ? (
                    <a
                      className="order-open-link"
                      href={adminUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                      title={adminTitle}
                      aria-label={adminTitle}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        width={14}
                        height={14}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden
                      >
                        <path d="M14 4h6v6" />
                        <path d="M10 14L20 4" />
                        <path d="M19 13v6a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h6" />
                      </svg>
                    </a>
                  ) : null}
                </div>
                <div className="d">
                  {o.lineItems[0]
                    ? `${o.lineItems[0].quantity}× ${o.lineItems[0].title}${
                        o.lineItems.length > 1 ? ` +${o.lineItems.length - 1}` : ''
                      }`
                    : 'No line items'}
                  <span className="order-date"> · {formatShortDate(o.createdAt)}</span>
                </div>
                <div className="s">{statusBadge(o.fulfillmentStatus, o.financialStatus)}</div>
              </div>
            );
          })}
        </div>
      ) : null}

      {/* Phase 9 — pending decision queue (damage/refund/reprint/address/cancel).
          Renders nothing when the queue is empty so the rail stays clean. */}
      <DecisionPanel conversationId={conversationId} />

      <div className="cust-notes">
        <div className="eyebrow">Private notes</div>
        <CustomerNoteBox customerEmail={customerEmail} />
      </div>

      <FullCustomerHistory
        customerEmail={customerEmail}
        currentConversationId={conversationId}
      />
    </aside>
  );
}
